import { useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Box } from '@material-ui/core';
import WifiOffIcon from '@material-ui/icons/WifiOff';
import WifiIcon from '@material-ui/icons/Wifi';

import { useOnlineStatus } from 'hooks';

const useStyles = makeStyles((theme) => ({
  offlineSnackbar: {
    backgroundColor: `${theme.palette.error.main} !important`,
    color: `${theme.palette.error.contrastText} !important`,
    '& .SnackbarContent-root': {
      backgroundColor: `${theme.palette.error.main} !important`,
      borderRadius: '8px !important',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15) !important',
    },
    '& .SnackbarItem-message': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
  },
  onlineSnackbar: {
    backgroundColor: `${theme.palette.success.main} !important`,
    color: `${theme.palette.success.contrastText} !important`,
    '& .SnackbarContent-root': {
      backgroundColor: `${theme.palette.success.main} !important`,
      borderRadius: '8px !important',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15) !important',
    },
    '& .SnackbarItem-message': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
  },
  messageContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  icon: {
    fontSize: '1.2rem',
    animation: '$pulse 2s infinite',
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.6,
    },
    '100%': {
      opacity: 1,
    },
  },
}));

function ConnectionStatusNotifier() {
  const classes = useStyles();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const isOnline = useOnlineStatus();
  const offlineSnackbarRef = useRef(null);
  const onlineSnackbarRef = useRef(null);
  const wasOfflineRef = useRef(false);

  useEffect(() => {
    if (!isOnline) { // Usuario se desconectó
      wasOfflineRef.current = true;
      
      // Cerrar cualquier notificación de conexión anterior
      if (onlineSnackbarRef.current) {
        closeSnackbar(onlineSnackbarRef.current);
        onlineSnackbarRef.current = null;
      }

      offlineSnackbarRef.current = enqueueSnackbar(
        <Box className={classes.messageContainer}>
          <WifiOffIcon className={classes.icon} />
          <Typography variant="body2" component="span">
            No hay conexión a internet. Verifica tu conexión.
          </Typography>
        </Box>,
        {
          variant: 'error',
          persist: true,
          preventDuplicate: true,
          className: classes.offlineSnackbar,
        },
      );
    } else {
      if (wasOfflineRef.current && offlineSnackbarRef.current) {
        closeSnackbar(offlineSnackbarRef.current); // Cerrar notificación de desconexión
        offlineSnackbarRef.current = null;

        onlineSnackbarRef.current = enqueueSnackbar(
          <Box className={classes.messageContainer}>
            <WifiIcon className={classes.icon} />
            <Typography variant="body2" component="span">
              ¡Conexión restablecida correctamente!
            </Typography>
          </Box>,
          {
            variant: 'success',
            autoHideDuration: 3000,
            preventDuplicate: true,
            className: classes.onlineSnackbar,
          },
        );

        wasOfflineRef.current = false;
      }
    }

    return () => {
      if (offlineSnackbarRef.current) {
        closeSnackbar(offlineSnackbarRef.current);
      }
      if (onlineSnackbarRef.current) {
        closeSnackbar(onlineSnackbarRef.current);
      }
    };
  }, [isOnline, enqueueSnackbar, closeSnackbar, classes]);

  return null;
}

export default ConnectionStatusNotifier;
